import React, { useState, useContext } from 'react';
import axios from 'axios';
import {
	Avatar,
	Menu,
	MenuItem,
	Typography,
	Divider,
	Box,
	Paper,
	Tooltip,
	Button,
	Popover,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Skeleton,
	CircularProgress
} from '@mui/material';
import {
	AccountCircle as AccountCircleIcon,
	Logout as LogoutIcon,
	HelpOutline as HelpIcon,
	FiberManualRecord as DotIcon,
	KeyboardArrowDown as DownIcon,
	KeyboardArrowUp as UpIcon,
	Check as CheckIcon
} from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import { AuthContext, useNavigate, usePlans } from 'components/lib';
import Styles from './user.module.scss'
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';

export function User(props) {
	const navigate = useNavigate();
	const authContext = useContext(AuthContext);
	const plans = usePlans()
	const user = authContext?.user
	const currentPlan = plans?.data?.raw?.plans?.find(plan => plan.id === authContext.user.plan)

	return (
		<div className={Styles.user}>
			{plans.loading ? (
				<Skeleton
					variant="rectangular"
					width={150}
					height={36}
					sx={{ borderRadius: '50px' }}
				/>
			) : (
				<PlanPillWithDropdown
					currentPlan={currentPlan}
					onUpgradeClick={() => navigate('/account/upgrade')}
					onManageClick={() => navigate('/account/billing')}
					onSetupClick={() => navigate('/account')}
					user={user}
				/>
			)}
			<UserAvatar
				userDetails={user}
				onProfileClick={() => navigate('/account/profile')}
				onAccountClick={() => navigate('/account')}
				onHelpClick={() => navigate('/help')}
				onLogoutClick={() => authContext.signout()}
			/>
		</div>
	);
}


function UserAvatar({
	userDetails,
	onProfileClick,
	onAccountClick,
	onHelpClick,
	onLogoutClick
}) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [userScore, setUserScore] = useState(null);
	const [scoreLoading, setScoreLoading] = useState(false);

	const open = Boolean(anchorEl);

	const handleMenuOpen = async (event) => {
		setAnchorEl(event.currentTarget);
		setScoreLoading(true);
		try {
			const { data } = await axios.get('/api/user');
			setUserScore(data.user_score);
		} catch (err) {
			console.error("Failed to fetch user score", err);
			setUserScore(0); // fallback
		}
		setScoreLoading(false);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	// Handler wrappers that close the menu after action
	const handleProfileClick = () => {
		onProfileClick();
		handleMenuClose();
	};

	const handleAccountClick = () => {
		onAccountClick();
		handleMenuClose();
	};

	const handleHelpClick = () => {
		onHelpClick();
		handleMenuClose();
	};

	const handleLogout = () => {
		onLogoutClick();
		handleMenuClose();
	};

	const isScoreBadge = userDetails?.role_permissions['scorebadge.view'];
	return (
		<>
			<Tooltip title={"Profile Settings"} arrow>
				<Avatar
					sx={{
						bgcolor: '#2e6bd6',
						width: 40,
						height: 40,
						cursor: 'pointer',
						border: '2px solid #f8fafc'
					}}
					onClick={handleMenuOpen}
				>
					<PersonIcon sx={{ fontSize: 20 }} />
				</Avatar>
			</Tooltip>

			{/* Enhanced Menu */}
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					elevation: 3,
					sx: {
						width: 300,
						borderRadius: 2,
						mt: 1,
						overflow: 'visible',
						'&:before': {
							content: '""',
							position: 'absolute',
							top: -10,
							right: 14,
							width: 20,
							height: 20,
							bgcolor: '#f8fafc',
							transform: 'rotate(45deg)',
							zIndex: 0,
						},
					}
				}}
			>
				{/* Profile Header */}
				<Paper
					elevation={0}
					sx={{
						bgcolor: '#f8fafc',
						borderRadius: '16px 16px 0 0',
						padding: '20px',
						pb: 3,
						cursor: 'pointer'
					}}
					onClick={handleProfileClick}
				>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Avatar
							sx={{
								bgcolor: '#2e6bd6',
								width: 60,
								height: 60,
								fontSize: 24
							}}
						>
							<PersonIcon sx={{ fontSize: 30 }} />
						</Avatar>

						<Box sx={{ ml: 2, flexGrow: 1 }}>
							<Tooltip title={userDetails?.name} arrow>
								<Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b', maxWidth: '12ch', textOverflow: 'ellipsis', overflow: 'clip' }}>
									{userDetails?.name}
								</Typography>
							</Tooltip>

							{userDetails?.accounts[0]?.name && !userDetails?.role_permissions['account.name.view'] && (
								<Tooltip title={userDetails?.accounts[0]?.name} arrow>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{
											maxWidth: "18ch",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "pre-wrap",
										}}
									>
										{userDetails?.accounts[0]?.name}
									</Typography>
								</Tooltip>
							)}

							<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
								@{userDetails?.role?.toLowerCase()}
							</Typography>
						</Box>

						{/* Score Badge */}
						{isScoreBadge && (
							<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
									SCORE
								</Typography>
								{scoreLoading ? (
									<CircularProgress size={30} />
								) : (
									<Avatar
										sx={{
											width: 30,
											height: 30,
											bgcolor: '#2e6bd6',
											fontSize: 14,
											fontWeight: 'bold'
										}}
									>
										{userScore ?? userDetails?.user_score ?? 0}
									</Avatar>
								)}
							</Box>
						)}
					</Box>
				</Paper>

				<Divider />

				{/* Menu Items with Icons */}
				<MenuItem
					onClick={handleAccountClick}
					sx={{ py: 1.5, px: 3 }}
				>
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						borderRadius: '50%',
						bgcolor: '#f1f5f9',
						p: 1,
						mr: 2
					}}>
						<AccountCircleIcon color="action" />
					</Box>
					<Typography>Account</Typography>
				</MenuItem>

				<MenuItem
					onClick={handleHelpClick}
					sx={{ py: 1.5, px: 3 }}
				>
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						borderRadius: '50%',
						bgcolor: '#f1f5f9',
						p: 1,
						mr: 2
					}}>
						<HelpIcon color="action" />
					</Box>
					<Typography>Help</Typography>
				</MenuItem>

				<MenuItem
					onClick={handleLogout}
					sx={{ py: 1.5, px: 3 }}
				>
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						borderRadius: '50%',
						bgcolor: '#f1f5f9',
						p: 1,
						mr: 2
					}}>
						<LogoutIcon sx={{ color: 'red' }} />
					</Box>
					<Typography sx={{ color: 'red' }}>Logout</Typography>
				</MenuItem>
			</Menu>
		</>
	);
}

function PlanPillWithDropdown({ currentPlan, onUpgradeClick, onManageClick, onSetupClick, user }) {
	const [anchorEl, setAnchorEl] = useState(null);

	const handleClick = (event) => {
		if (currentPlan?.enterprise) return;
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleUpgrade = () => {
		onUpgradeClick();
		handleClose();
	};

	const handleManageClick = () => {
		onManageClick();
		handleClose();
	}

	const handleProfileSetup = () => {
		onSetupClick();
		handleClose();
	}

	const open = Boolean(anchorEl);
	const id = open ? 'plan-popover' : undefined;

	const getDisplayFeatures = (featureList) => {
		return featureList?.slice(0, 3);
	}

	return (
		<>
			<Button
				onClick={handleClick}
				variant="outlined"
				startIcon={<DotIcon style={{ fontSize: '12px' }} />}
				endIcon={!currentPlan?.enterprise && (open ? <UpIcon /> : <DownIcon />)}
				color='success'
				sx={{
					borderRadius: '50px',
					textTransform: 'none',
					px: 2,
					py: 0.5,
					backgroundColor: '#dcfce7',
					cursor: currentPlan?.enterprise ? 'default' : 'pointer'
				}}
			>
				{currentPlan?.name}
			</Button>

			{!currentPlan?.enterprise && (
				<Popover
					id={id}
					open={open}
					anchorEl={anchorEl}
					onClose={handleClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					PaperProps={{
						sx: {
							mt: 0.5,
							borderRadius: '8px'
						}
					}}
				>
					<Paper sx={{ p: 3, width: 280 }}>
						<Typography variant="h6" component="h3" gutterBottom>
							Your Plan: {currentPlan?.name}
						</Typography>

						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Plan features:
						</Typography>

						<List dense sx={{ mt: 0.5 }}>
							{getDisplayFeatures(currentPlan?.features)?.map((feature, index) => (
								<ListItem key={index} disablePadding sx={{ py: 0.5 }}>
									<ListItemIcon sx={{ minWidth: 32 }}>
										<CheckIcon fontSize="small" color="success" />
									</ListItemIcon>
									<ListItemText primary={feature?.name} />
								</ListItem>
							))}

							{currentPlan?.features?.length > 3 && (
								<ListItem disablePadding sx={{ py: 0.5 }}>
									<ListItemText secondary="... And more" />
								</ListItem>
							)}
						</List>

						<Divider sx={{ my: 2 }} />

						{!user?.onboarded && (
							<Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
								Please complete your profile setup to upgrade your plan.
							</Typography>
						)}

						<AivagamButton
							type={user?.onboarded ? "confirm" : "action"}
							onClick={user?.onboarded ? handleUpgrade : handleProfileSetup}
							buttonText={user?.onboarded ? "Upgrade Plan" : "Setup Profile"}
							classStyles={{
								boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
								padding: '0.75em 2em',
								width: '100%'
							}}
						/>

						{!!user?.onboarded && (
							<Box sx={{ mt: 1, textAlign: 'center' }}>
								<Typography
									variant="caption"
									component="a"
									onClick={handleManageClick}
									sx={{
										textDecoration: 'underline',
										color: 'text.secondary',
										'&:hover': { color: 'primary.main' },
										cursor: 'pointer',
									}}
								>
									Manage subscription
								</Typography>
							</Box>
						)}
					</Paper>
				</Popover>
			)}
		</>
	);
}